import { AppState } from "../data/redux-state"
import { DPoint, getLineType, LineType, UncertaintyLine } from "./extractLines"
import { darkenColor } from "./lineUtils"

interface RenderArgs {
  appState: AppState
  chartBody: any
  line: UncertaintyLine
  color: string
  lineClass: string
  lineSelector: string
  xScale: d3.ScaleTime<number, number, never>
  yScale: d3.ScaleLinear<number, number, never>
  d3: any
  svg: any
}

export type ExplicitSymbolType = "circle" | "diamond" | "square"

// For EWM indicators a SubVariable's compVar may specify an explicit SymbolType
// in its JSON Metadata. Returns the normalized symbol type, or undefined when
// the override does not apply (non-EWM) or the value is unrecognized.
export function resolveEwmSymbolType(
  indicatorClass: string,
  symbolTypeRaw: string | undefined
): ExplicitSymbolType | undefined {
  if (!indicatorClass.startsWith("EWM")) {
    return undefined
  }
  const symbolType = symbolTypeRaw?.toLowerCase()
  if (
    symbolType === "circle" ||
    symbolType === "diamond" ||
    symbolType === "square"
  ) {
    return symbolType
  }
  return undefined
}

function getExplicitSymbolType(args: RenderArgs): ExplicitSymbolType | undefined {
  return resolveEwmSymbolType(
    args.appState.indicatorClass,
    args.line.coVar.Metadata.SymbolType
  )
}

function renderLineForExplicitSymbolType(
  args: RenderArgs,
  symbolType: ExplicitSymbolType
) {
  if (symbolType === "circle") {
    renderObservationLine(args)
  } else if (symbolType === "diamond") {
    renderDiamondsLine(args)
  } else {
    renderScenarioLine(args)
  }
}

function getSymbolScale(args: RenderArgs) {
  if (!args.appState.indicatorClass.startsWith("EWM")) {
    return 1
  }
  return args.line.coVar.Metadata.SymbolSize === "small" ? 0.75 : 1
}

function getObservationRadius(args: RenderArgs) {
  return 4 * getSymbolScale(args)
}

function getDiamondBaseSize(args: RenderArgs) {
  return size * getSymbolScale(args)
}

function getSDevCircleRadius(args: RenderArgs) {
  return 3 * getSymbolScale(args)
}

function getSDevConnectorStrokeWidth(args: RenderArgs) {
  return 1.5 * getSymbolScale(args)
}

const generateLine = ({
  props,
  index,
  svg,
  data,
  xScale,
  yScale,
  d3,
  clickDot,
  clickedDataArray,
  chartBody,
}: {
  props: AppState
  index: number
  svg: any
  data: UncertaintyLine[]
  xScale: d3.ScaleTime<number, number, never>
  yScale: d3.ScaleLinear<number, number, never>
  d3: any
  clickDot: (...args: any) => any
  clickedDataArray: DPoint[]
  chartBody: any
}) => {
  const line = data[index]
  const color = line.color
  const lineType: LineType = getLineType(line)
  const lineClass = getLineClassFromIndex(index)
  const lineSelector = getLineSelector(index, lineType)

  const renderArgs: RenderArgs = {
    appState: props,
    chartBody,
    line,
    color,
    lineClass,
    lineSelector,
    xScale,
    yScale,
    d3,
    svg,
  }

  // console.log("renderArgs", renderArgs)
  const explicitSymbolType = getExplicitSymbolType(renderArgs)
  if (lineType === "OBSERVATION") {
    if (explicitSymbolType) {
      renderLineForExplicitSymbolType(renderArgs, explicitSymbolType)
    } else {
      renderObservationLine(renderArgs)
    }
  } else if (lineType === "PATH_TO_PARIS") {
    renderScenarioLine(renderArgs)
  } else {
    const isAnnualPrediction = line.coVarKey.includes("andAnnPred")
    const isScenario = line.coVarKey.toLowerCase().includes("scen")
    if (isAnnualPrediction) {
      renderArgs.color = darkenColor(renderArgs.color, 0.8)
      renderObservationLine(renderArgs)
    } else if (explicitSymbolType) {
      renderLineForExplicitSymbolType(renderArgs, explicitSymbolType)
    } else if (!isScenario) {
      renderDiamondsLine(renderArgs)
    } else {
      renderScenarioLine(renderArgs)
    }
  }

  renderClickableAreas(renderArgs, data, clickDot, clickedDataArray)

  if (line.coVarOption?.sdevEnabled && line.sDevPoints && line.sDevPoints.length > 0) {
    if (line.coVarKey.includes("StDevAvsD")) {
      renderSDevAsCircleLines(renderArgs)
    } else {
      renderSDev(renderArgs)
    }
  }
}

function getLineSelector(index: number, lineType: LineType) {
  const lineClass = getLineClassFromIndex(index)
  if (lineType === "OBSERVATION") {
    return lineClass
  } else if (lineType === "PATH_TO_PARIS") {
    return `${lineClass}_p2p`
  } else {
    return `${lineClass}_other`
  }
}

function getLineClassFromIndex(index: number) {
  return "line_class_" + index
}

function renderClickableAreas(
  args: RenderArgs,
  data: UncertaintyLine[],
  clickDot: (...args: any) => any,
  clickedDataArray: DPoint[]
) {
  args.svg
    .selectAll("." + `${args.lineClass}_trans`)
    // .attr("clip-path", "url(#clip)")
    .data(args.line.mainPoints)
    .enter()
    .append("circle")
    .attr("class", `${args.lineClass}_trans`)
    .attr("cx", function (d: DPoint) {
      return args.xScale(d.date)
    })
    .attr("cy", function (d: DPoint) {
      return args.yScale(d.value)
    })
    .attr("fill", "transparent")
    .attr("r", 8)
    .on("click", (event: any) => {
      clickDot(
        event,
        data,
        args.xScale,
        args.yScale,
        args.line,
        args.svg,
        clickedDataArray,
        args.line.mainPoints
      )
    })
}

function renderObservationLine(args: RenderArgs) {
  const radius = getObservationRadius(args)
  args.chartBody
    .selectAll("." + args.lineSelector)
    .data(args.line.mainPoints)
    .enter()
    .append("circle")
    .attr("class", args.lineSelector)
    .attr("cx", function (d: DPoint) {
      return args.xScale(d.date)
    })
    .attr("cy", function (d: DPoint) {
      return args.yScale(d.value)
    })
    .attr("fill", args.color)
    .attr("r", radius)
}

const size = 8

function renderScenarioLine(args: RenderArgs) {
  const squareSize = 7
  args.chartBody
    .selectAll("." + args.lineSelector)
    .data(args.line.mainPoints)
    .enter()
    .append("rect")
    .attr("class", args.lineSelector)
    .attr("x", function (d: DPoint) {
      return args.xScale(d.date) - squareSize / 2
    })
    .attr("y", function (d: DPoint) {
      return args.yScale(d.value) - squareSize / 2
    })
    .attr("fill", args.color)
    .attr("width", squareSize)
    .attr("height", squareSize)
}

function renderDiamondsLine(args: RenderArgs) {
  const diamondBaseSize = getDiamondBaseSize(args)
  // we try to compute a diamondSize that would exactly fit into the area of a circle
  const diamondSizeFittingIntoCircle = Math.sqrt((diamondBaseSize * diamondBaseSize) / 2)
  const diamondScaleUp = 1.2
  const diamondSize = diamondScaleUp * diamondSizeFittingIntoCircle
  const diamondOffset = (diamondBaseSize / 2) * diamondScaleUp
  args.chartBody
    .selectAll("." + `${args.lineClass}_other`)
    .data(args.line.mainPoints)
    .enter()
    .append("rect")
    .attr("class", `${args.lineClass}_other`)
    .attr("x", function (d: DPoint) {
      return args.xScale(d.date) - diamondOffset
    })
    .attr("y", function (d: DPoint) {
      return args.yScale(d.value) - diamondOffset
    })
    .attr("transform", function (d: DPoint) {
      // this rotation changes the rect-elements to diamonds
      const x = args.xScale(d.date)
      const y = args.yScale(d.value)
      return `translate(${x}, ${y}) rotate(45) translate(${-x + diamondSize / 4}, ${-y + diamondSize / 4})`
    })
    .attr("fill", args.color)
    .attr("width", diamondSize)
    .attr("height", diamondSize)
}

function renderSDevAsCircleLines(args: RenderArgs) {
  const symbolSize = getSDevCircleRadius(args)

  // Circle markers at upper bound positions
  args.chartBody
    .selectAll("." + `${args.lineClass}_sdev_circle_up`)
    .data(args.line.sDevPoints!.filter((d: DPoint) => !isNaN(d.up!.value)))
    .enter()
    .append("circle")
    .attr("class", `${args.lineClass}_sdev_circle_up`)
    .attr("cx", function (d: DPoint) {
      return args.xScale(d.date)
    })
    .attr("cy", function (d: DPoint) {
      return args.yScale(d.up!.value)
    })
    .attr("r", symbolSize)
    .attr("fill", args.color)

  // Circle markers at lower bound positions
  args.chartBody
    .selectAll("." + `${args.lineClass}_sdev_circle_down`)
    .data(args.line.sDevPoints!.filter((d: DPoint) => !isNaN(d.down!.value)))
    .enter()
    .append("circle")
    .attr("class", `${args.lineClass}_sdev_circle_down`)
    .attr("cx", function (d: DPoint) {
      return args.xScale(d.date)
    })
    .attr("cy", function (d: DPoint) {
      return args.yScale(d.down!.value)
    })
    .attr("r", symbolSize)
    .attr("fill", args.color)
}

function renderSDev(args: RenderArgs) {
  const sDevConnectorStrokeWidth = getSDevConnectorStrokeWidth(args)
  args.chartBody
    .selectAll("." + `${args.lineClass}_sdrvplus`)
    .data(args.line.sDevPoints)
    .enter()
    .append("rect")
    .attr("class", `${args.lineClass}_sdrvplus`)
    .attr("x", function (d: DPoint) {
      return args.xScale(d.date) - size / 2
    })
    .attr("y", function (d: DPoint) {
      return args.yScale(d.up!.value) - size / 4
    })
    .attr("fill", args.color)
    .attr("width", size)
    .attr("height", 2)

  args.chartBody
    .selectAll("." + `${args.lineClass}_sdrvminus`)
    .data(args.line.sDevPoints)
    .enter()
    .append("rect")
    .attr("class", `${args.lineClass}_sdrvminus`)
    .attr("x", function (d: DPoint) {
      return args.xScale(d.date) - size / 2
    })
    .attr("y", function (d: DPoint) {
      return args.yScale(d.down!.value) - size / 4
    })
    .attr("fill", args.color)
    .attr("width", size)
    .attr("height", 2)

  args.line.sDevPoints!.forEach((p: DPoint, i: number) => {
    const connectedPoints: DPoint[] = [p.down!, p.up!]
    args.chartBody
      .append("path")
      .datum(connectedPoints)
      .attr("fill", "none")
      .attr("stroke", args.color)
      .attr("stroke-width", sDevConnectorStrokeWidth)
      .attr("class", "line_three")
      .attr(
        "d",
        args.d3
          .line()
          .x((d: DPoint) => {
            return args.xScale(d.date)
          })
          .y((d: DPoint) => {
            return args.yScale(d.value)
          })
      )
  })
}

export default generateLine
